import { useEffect, useCallback } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';

export const useAuth = () => {
  const authStore = useAuthStore();
  const navigate = useNavigate();

  // TEMPORARILY DISABLED ALL PERIODIC VALIDATION
  // Only manual validation on critical operations
  useEffect(() => {
    // All periodic validation disabled until 429 issues are resolved
    // Manual validation will be used only when absolutely necessary
  }, []);

  // Enhanced logout that clears everything
  const logout = useCallback(async () => {
    try {
      await authStore.logout();
      // Clear any remaining auth data from localStorage
      localStorage.removeItem('dasoonegg-auth');
      console.log('[useAuth] Logout completed successfully');
    } catch (error) {
      console.error('[useAuth] Error during logout:', error);
      // Still clear localStorage even if logout fails
      localStorage.removeItem('dasoonegg-auth');
    }
    navigate('/login');
  }, [authStore, navigate]);

  // Enhanced login that validates the token
  const login = useCallback(async (authData: { token: string; refreshToken: string; user: any }) => {
    try {
      await authStore.login(authData);
      
      // Validate the token immediately after login
      const isValid = await authStore.validateAndRefreshToken();
      if (!isValid) {
        console.error('[useAuth] Token validation failed immediately after login');
        await logout();
        return false;
      }
      
      console.log('[useAuth] Login completed and validated successfully');
      return true;
    } catch (error) {
      console.error('[useAuth] Error during login:', error);
      await logout();
      return false;
    }
  }, [authStore, logout]);

  // Guest login with validation
  const loginAsGuest = useCallback(async (guestData: { guestId: string; sessionToken: string }) => {
    try {
      await authStore.loginAsGuest(guestData);
      
      // For guest tokens, we don't refresh but we validate expiry
      if (authStore.isTokenExpired()) {
        console.error('[useAuth] Guest token is already expired');
        await logout();
        return false;
      }
      
      console.log('[useAuth] Guest login completed successfully');
      return true;
    } catch (error) {
      console.error('[useAuth] Error during guest login:', error);
      await logout();
      return false;
    }
  }, [authStore, logout]);

  // Check if current session is valid
  const isSessionValid = useCallback(() => {
    if (!authStore.isAuthenticated) {
      return false;
    }
    
    return !authStore.isTokenExpired();
  }, [authStore]);

  return {
    ...authStore,
    login,
    loginAsGuest,
    logout,
    isSessionValid,
  };
};

// Hook for components that require authentication
export const useRequireAuth = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.isSessionValid()) {
      console.log('[useRequireAuth] Authentication required, redirecting to login');
      navigate('/login');
    }
  }, [auth.isAuthenticated, auth.isSessionValid, navigate]);

  return auth;
};

// Hook for protecting admin routes
export const useRequireAdmin = () => {
  const auth = useRequireAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated && auth.user && auth.user.type !== 'ADMIN') {
      console.log('[useRequireAdmin] Admin access required, redirecting');
      navigate('/');
    }
  }, [auth.isAuthenticated, auth.user, navigate]);

  return auth;
};